import { FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, Check, Eye, EyeOff, KeyRound, Loader2, Mail, ShieldAlert, User } from 'lucide-react';
import { invitesAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Public page for invite links (2026-08-31, Jaime): backend invites service
// generates `${APP_URL || https://vecindus.com}/accept-invite?token=<raw>`.
// GET /api/invites/:token is public → { success, data: { appName, inviter,
// invitee: { name, email }, role, unitLabel, expiresAt, accepted, expired, canAccept } }.
// POST /api/invites/:token/accept { password } → sets first password (single use).
interface InviteInfo {
  appName?: string;
  inviter?: string | null;
  invitee?: { name?: string | null; email?: string | null };
  role?: string | null;
  unitLabel?: string | null;
  expiresAt?: string | null;
  accepted?: boolean;
  expired?: boolean;
  canAccept?: boolean;
}

function roleDescription(role?: string | null): string {
  const r = String(role || '').toLowerCase();
  if (r.includes('resident')) return 'Acceso como residente de la comunidad.';
  if (r.includes('empleado') || r.includes('staff') || r.includes('personal')) return 'Acceso como personal del condominio.';
  return 'Acceso a tu cuenta de Vecindus.';
}

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [info, setInfo] = useState<InviteInfo | null>(null);

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState(false);

  // Occupation link fallback (onboarding/frontend-enable, 2026-10-01): an
  // admin-generated occupation link (/api/onboarding/occupation-links) shares
  // the ?token= URL shape. When the invites lookup fails, try the PUBLIC
  // GET /api/onboarding/occupation-links/:token
  // -> { data: { mode: 'unit'|'open', condoName, unitNumber? } } and render the
  // occupation accept flow instead (login required; POST .../accept).
  const [occInfo, setOccInfo] = useState<{ mode: 'unit' | 'open'; condoName?: string; unitNumber?: string | null } | null>(null);
  const [occUnitNumber, setOccUnitNumber] = useState('');
  const [occBusy, setOccBusy] = useState(false);
  const [occDone, setOccDone] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        if (!cancelled) { setLoadError('El enlace de invitación no es válido.'); setLoading(false); }
        return;
      }
      try {
        const res = await invitesAPI.getInfo(token);
        if (!cancelled) setInfo(res.data?.data || res.data || null);
      } catch (inviteErr: any) {
        try {
          const res = await api.get(`/onboarding/occupation-links/${encodeURIComponent(token)}`);
          const data = res.data?.data;
          if (!cancelled && data?.valid) setOccInfo({ mode: data.mode || 'open', condoName: data.condoName, unitNumber: data.unitNumber });
          else if (!cancelled) setLoadError('No pudimos validar tu invitación.');
        } catch (err: any) {
          if (!cancelled) setLoadError(err?.response?.data?.error || inviteErr?.response?.data?.error || 'No pudimos validar tu invitación.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // LIVE backend: POST /api/onboarding/occupation-links/accept
  // { token, unitNumber? (mode 'open') } -> binds the unit + propietario role.
  const handleOccupationAccept = async (event: FormEvent) => {
    event.preventDefault();
    setFormError('');
    if (occInfo?.mode === 'open' && !occUnitNumber.trim()) {
      setFormError('Ingresa el número de tu unidad (ej. 101, TH1, A-3).');
      return;
    }
    setOccBusy(true);
    try {
      const res = await api.post('/onboarding/occupation-links/accept', {
        token,
        ...(occInfo?.mode === 'open' ? { unitNumber: occUnitNumber.trim() } : {}),
      });
      const data = res.data?.data;
      setOccDone([data?.condoName, data?.unitNumber ? `Unidad ${data.unitNumber}` : ''].filter(Boolean).join(' · '));
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const apiError = err?.response?.data?.error;
      if (code === 'UNIT_OCCUPIED') setFormError('Esta unidad ya fue ocupada o ya tiene propietario.');
      else if (code === 'DUPLICATE_UNIT_NUMBER') setFormError('Ya existe una unidad con ese número en este condominio.');
      else if (code === 'UNIT_CAP_REACHED') setFormError('Límite de unidades del condominio alcanzado.');
      else setFormError(typeof apiError === 'string' && apiError ? apiError : 'No se pudo aceptar el enlace. Puede haber expirado o sido utilizado.');
    } finally { setOccBusy(false); }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError('');
    if (password.length < 8) return setFormError('La contraseña debe tener al menos 8 caracteres.');
    if (password !== confirmation) return setFormError('Las contraseñas no coinciden.');
    setSubmitting(true);
    try {
      await invitesAPI.accept(token, password);
      setSuccess(true);
    } catch (err: any) {
      setFormError(err?.response?.data?.error || 'No pudimos establecer tu contraseña. El enlace pudo haber expirado.');
      // Refresh state: the invite may have been consumed/expired meanwhile.
      try {
        const res = await invitesAPI.getInfo(token);
        setInfo(res.data?.data || res.data || null);
      } catch { /* keep previous info */ }
    } finally {
      setSubmitting(false);
    }
  };

  const unusable = !!info && (info.accepted || info.expired || !info.canAccept);

  return (
    <div className="min-h-screen bg-[var(--v-base-bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8 text-[var(--v-teal-400)]">
          <Building2 className="w-7 h-7" />
          <span className="text-2xl font-bold tracking-tight text-white">Vecindus</span>
        </div>

        <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-2xl border border-[var(--v-border-light)] p-8">
          {loading && (
            <div className="flex flex-col items-center gap-3 py-8 text-[var(--v-text-secondary)]">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--v-teal-400)]" />
              <p className="text-sm">Validando tu invitación…</p>
            </div>
          )}

          {!loading && (loadError || !info) && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <ShieldAlert className="w-8 h-8 text-red-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2">Invitación no válida</h1>
              <p className="text-sm text-[var(--v-text-secondary)] mb-8">{loadError || 'El enlace de invitación no es válido.'}</p>
              <Link to="/login" className="block w-full text-center py-3 bg-[var(--v-teal-400)] text-white font-semibold rounded-xl hover:opacity-90">Ir al inicio de sesión</Link>
            </div>
          )}

          {!loading && info && unusable && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <ShieldAlert className="w-8 h-8 text-amber-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2">
                {info.accepted ? 'Invitación ya utilizada' : 'Invitación expirada'}
              </h1>
              <p className="text-sm text-[var(--v-text-secondary)] mb-8">
                {info.accepted
                  ? 'Esta invitación ya fue utilizada. Si ya estableciste tu contraseña, puedes iniciar sesión normalmente.'
                  : 'Esta invitación expiró. Solicita a quien te invitó que envíe un nuevo enlace.'}
              </p>
              <Link to="/login" className="block w-full text-center py-3 bg-[var(--v-teal-400)] text-white font-semibold rounded-xl hover:opacity-90">Ir al inicio de sesión</Link>
            </div>
          )}

          {!loading && info && !unusable && !success && (
            <>
              <h1 className="text-2xl font-bold text-white text-center mb-2">Bienvenido a Vecindus</h1>
              <p className="text-sm text-[var(--v-text-secondary)] text-center mb-6">
                {info.inviter ? `${info.inviter} te invitó a formar parte de la comunidad.` : 'Te invitaron a formar parte de la comunidad.'}
              </p>

              <div className="space-y-2 mb-6 text-sm">
                {info.invitee?.name && (
                  <div className="flex items-center gap-3 rounded-lg bg-[var(--v-surface-2)] border border-[var(--v-border-light)] px-4 py-3">
                    <User className="w-4 h-4 text-[var(--v-teal-400)] shrink-0" />
                    <span className="text-[var(--v-text-primary)] break-all">{info.invitee.name}</span>
                  </div>
                )}
                {info.invitee?.email && (
                  <div className="flex items-center gap-3 rounded-lg bg-[var(--v-surface-2)] border border-[var(--v-border-light)] px-4 py-3">
                    <Mail className="w-4 h-4 text-[var(--v-teal-400)] shrink-0" />
                    <span className="text-[var(--v-text-primary)] break-all">{info.invitee.email}</span>
                  </div>
                )}
                {info.unitLabel && (
                  <div className="flex items-center gap-3 rounded-lg bg-[var(--v-surface-2)] border border-[var(--v-border-light)] px-4 py-3">
                    <Building2 className="w-4 h-4 text-[var(--v-teal-400)] shrink-0" />
                    <span className="text-[var(--v-text-primary)]">{info.unitLabel}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 rounded-lg bg-[var(--v-surface-2)] border border-[var(--v-border-light)] px-4 py-3">
                  <KeyRound className="w-4 h-4 text-[var(--v-teal-400)] shrink-0" />
                  <span className="text-[var(--v-text-secondary)]">{roleDescription(info.role)}</span>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleSubmit}>
                <label className="block text-sm font-medium text-[var(--v-text-primary)]">
                  Crea tu contraseña
                  <div className="relative mt-2">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--v-text-secondary)]" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      minLength={8}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-white placeholder-[var(--v-text-secondary)] focus:outline-none focus:border-[var(--v-teal-400)]"
                      placeholder="Mínimo 8 caracteres"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--v-text-secondary)] hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </label>
                <label className="block text-sm font-medium text-[var(--v-text-primary)]">
                  Confirmar contraseña
                  <input
                    type={showPassword ? 'text' : 'password'}
                    minLength={8}
                    required
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    className="w-full mt-2 px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-white placeholder-[var(--v-text-secondary)] focus:outline-none focus:border-[var(--v-teal-400)]"
                    placeholder="Repite tu contraseña"
                    autoComplete="new-password"
                  />
                </label>
                {formError && <p className="text-sm text-red-400" role="alert">{formError}</p>}
                <button type="submit" disabled={submitting} className="w-full py-3 bg-[var(--v-teal-400)] text-white font-semibold rounded-xl hover:opacity-90 disabled:opacity-50">
                  {submitting ? 'Guardando…' : 'Establecer contraseña'}
                </button>
              </form>
            </>
          )}

          {!loading && occInfo && !success && (
            <div className="py-4">
              <h1 className="text-2xl font-bold text-white text-center mb-2">Reclama tu unidad</h1>
              <p className="text-sm text-[var(--v-text-secondary)] text-center mb-6">
                {occInfo.condoName ? `Administración de ${occInfo.condoName} comparte este enlace contigo.` : 'La administración del condominio comparte este enlace contigo.'}
                {occInfo.mode === 'unit' && occInfo.unitNumber ? ` Unidad asignada: ${occInfo.unitNumber}.` : ' Al aceptar, tu unidad se creará y quedarás como su propietario.'}
              </p>
              {occDone ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h2 className="text-xl font-bold text-white mb-2">¡Bienvenido!</h2>
                  <p className="text-sm text-[var(--v-text-secondary)] mb-8">
                    {occDone ? `Ya eres propietario de tu unidad${occDone ? ` en ${occDone}` : ''}.` : 'Ya eres propietario de tu unidad.'}
                  </p>
                  <Link to="/home/dashboard" className="block w-full text-center py-3 bg-[var(--v-teal-400)] text-white font-semibold rounded-xl hover:opacity-90">Ir a mi panel</Link>
                </div>
              ) : !user ? (
                <div className="text-center">
                  <p className="text-sm text-[var(--v-text-secondary)] mb-6">Inicia sesión con tu cuenta de Vecindus para aceptar este enlace y reclamar tu unidad.</p>
                  <Link to="/login" className="block w-full text-center py-3 bg-[var(--v-teal-400)] text-white font-semibold rounded-xl hover:opacity-90">Ir al inicio de sesión</Link>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleOccupationAccept}>
                  {occInfo.mode === 'open' && (
                    <label className="block text-sm font-medium text-[var(--v-text-primary)]">
                      Número de tu unidad
                      <input
                        required
                        maxLength={40}
                        value={occUnitNumber}
                        onChange={(e) => setOccUnitNumber(e.target.value)}
                        className="w-full mt-2 px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-white placeholder-[var(--v-text-secondary)] focus:outline-none focus:border-[var(--v-teal-400)]"
                        placeholder="Ej. 101, TH1, A-3"
                      />
                    </label>
                  )}
                  {formError && <p className="text-sm text-red-400" role="alert">{formError}</p>}
                  <button type="submit" disabled={occBusy} className="w-full py-3 bg-[var(--v-teal-400)] text-white font-semibold rounded-xl hover:opacity-90 disabled:opacity-50">
                    {occBusy ? 'Aceptando…' : 'Aceptar y reclamar mi unidad'}
                  </button>
                </form>
              )}
            </div>
          )}

          {!loading && info && !unusable && success && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2">¡Listo!</h1>
              <p className="text-sm text-[var(--v-text-secondary)] mb-8">
                Tu contraseña se estableció correctamente. Ya puedes iniciar sesión con tu cuenta.
              </p>
              <Link to="/login" className="block w-full text-center py-3 bg-[var(--v-teal-400)] text-white font-semibold rounded-xl hover:opacity-90">Iniciar sesión</Link>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-[var(--v-text-secondary)] mt-6">
          ¿Ya tienes cuenta? <Link to="/login" className="text-[var(--v-teal-400)] hover:underline">Inicia sesión aquí</Link>
        </p>
      </div>
    </div>
  );
}
